import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Dropdown, Menu, Divider } from 'antd';
import { useDispatch } from 'react-redux';
import { CopiesIcon, ExpandIcon, PdfIcon } from 'src/icons';
import AmbientMicIcon from 'src/assets/ambient_mic.svg';
import {
  useAuth,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import {
  LoadingOutlined,
  LockOutlined,
  SwapOutlined,
  FileAddOutlined,
} from '@ant-design/icons';
import { doStream, endStream } from '../../utils/listen';
import { Spin } from 'antd';
import MedicalConversationBox from '../baseComponents/MedicalConversationBox';
import moment from 'moment';
import CurrentTranscript from '../baseComponents/CurrentTranscript';
import { Button } from '../baseComponents';
import { EncounterSummaryMagicEdit } from '../CurrentEncounter/Encounter.Summary.MagicEdit';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  partialResetEncounterState,
  resetEncounterState,
  selectedEncounterSlice,
  setMobileRecord,
} from '../../store/slices/encounter';
import { useSelector } from 'react-redux';
import {
  getEncounter,
  getTranscription,
  listPinnedEncounters,
  updateEncounter,
} from '../../store/actions/encounter';
import { toast } from '@insight-health-ai/roseate/components';
import {
  exportSummaryPDF,
  listSummaries,
  rollbackSummary,
  updateSummary,
} from '../../store/actions/summary';
import { storeInLocal } from 'src/lib/storage';
import { MobileLiveTranscription } from './MobileLiveTranscription';
import { resetSummaryState } from '../../store/slices/summary';
import { SummaryUpdatedStatus } from './SummaryUpdatedStatus';
import { SegmentedTabs } from './SegmentedTabs';
import { EncounterPatient } from './AddPatientModalV2';
import EncounterOrders from '../Orders/EncounterOrders';
import { Tooltip } from 'antd';
import { DictateMic, DictateStopIcon } from 'src/icons';
import { ProIcon } from 'src/icons/Pro.icon';
import { LockIcon } from 'src/icons/Lock.icon';
import { UpgradeTooltip } from '../Payments/UpgradeTooltip';
import { CalanderAiIcon } from 'src/icons/CalendarAi.icon';
import { ICD10Coding } from './ICD10Coding';
import CPTCodes from './CPTCodes';
import MicrophoneBar from './MicrophoneBar';
import { ResumeIcon } from 'src/icons';
import {
  formatEncounterStatus,
  getEncounterStatus,
} from '../../utils/encounterHelper';
import { isAllowedToStartEncounter } from './permissions';
import { analytics } from 'src/services';
import {
  copyToClipboard,
  copyRichTextToClipboard,
  sanitizeSummaryAsHtml,
  sanitizeSummaryAsText,
} from 'src/utils';
import { LanguageSelector } from './LanguageSelector';
import { submitDocumentToEHRIntegration } from '../../../EHR/store/actions/ehr';
import EHRResponseModal from './EHRResponseModal';
import { useGetOrganizationEhrSettings } from 'src/hooks/settings';
import { useVimOsContext } from 'src/hooks/vim/hooks/useVimOsContext';
import { useUpdateEncounter } from 'src/hooks/vim/hooks/useUpdateEncounter';
import { useMicrophoneBadge } from 'src/hooks/vim/hooks/useMicrophoneBadge';
import { updateEncounterWithVimMappings } from '../../store/actions/ehr';
import EHRSubmissionDropdown from './EHRSubmissionDropdown';
import { useIsOP } from 'src/hooks/misc/useTenant';
import { logger } from 'src/services';
import { usePageHeader } from 'src/hooks/misc';
import TemplateTableWrapper from '../baseComponents/DocumentGenerationWrapper';
import { clearTemplateError } from '../../store/slices/templateSlice';
import { ChevronLeft } from '@insight-health-ai/roseate/icons';

const SetPageHeader = () => {
  const dispatch = useDispatch();
  const { ehrSettings } = useGetOrganizationEhrSettings();
  const isVim = ehrSettings?.ehr_system === 'vim';
  usePageHeader({
    title: 'Encounter Details',
    breadcrumb: isVim
      ? []
      : [
          {
            icon: <ChevronLeft />,
            title: 'Back to Encounters',
            href: '/encounters',
            onClick: () => {
              dispatch(setMobileRecord(false));
            },
          },
        ],
  });
  return null;
};
import { useEhrSaveDelay } from 'src/hooks/embedded/useEhrSaveDelay';

const MobileEncounterDetails = ({
  topBarInputs,
  storedParams,
  schedulepage,
  encounter_id = null,
  activeTab = '',
  setActiveButton: externalSetActiveButton,
  restrictTemplates = false,
  suggestions = {},
  patientDetails,
  autoResumeVisitEnabled,
  onAutoResumeComplete,
  isWrapped = false,
}) => {
  const { ehrSettings } = useGetOrganizationEhrSettings();
  const isOP = useIsOP();

  const { isLocked: isEhrSaveLocked } = useEhrSaveDelay(
    ehrSettings?.ehr_save_delay_ms
  );
  const isEhrSaveDelayed = isOP && isEhrSaveLocked;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isEntitled: isDictationEntitled } =
    useFeatureEntitlements('dictation');
  const { isEntitled: isOPPushEhrEnabled } = useFeatureEntitlements(
    'is_op_push_ehr_enabled'
  );
  const { isEntitled: hasICD10Access } = useFeatureEntitlements('icd-10');
  const { isEntitled: hasCPTAccess } = useFeatureEntitlements('cpt');
  const params = useParams();

  // Initialize encounter_id from params if not provided
  encounter_id = encounter_id || params.encounter_id;
  const { encounterDetails, transcriptionbyIdValue } = useSelector(
    (state) => state?.encounterState
  );
  const { currentPractitioner, currentPractitionerSettings } = useSelector(
    (state) => state?.practitionerState
  );
  const vimOS = useVimOsContext();
  const canUpdateParam = useMemo(
    () => ({
      subjective: {
        chiefComplaintNotes: true,
        generalNotes: true,
        historyOfPresentIllnessNotes: true,
        reviewOfSystemsNotes: true,
      },
      objective: {
        generalNotes: true,
        physicalExamNotes: true,
      },
      patientInstructions: {
        generalNotes: true,
      },
      plan: {
        generalNotes: true,
      },
      assessment: {
        generalNotes: true,
        diagnosisCodes: true,
      },
      encounterNotes: {
        generalNotes: true,
      },
      billingInformation: {
        procedureCodes: true,
      },
    }),
    []
  );
  const { canUpdateParams, canUpdate, updateVimEncounter, updateableDetails } =
    useUpdateEncounter(canUpdateParam);
  // State hooks
  const [liveTranscript, setLiveTranscript] = useState([]);
  const [livePartialTranscript, setLivePartialTranscript] = useState('');
  const [fullTranscript, setFullTranscript] = useState([]);
  const [recordingTabs, setRecordingTabs] = useState([]);
  const [encounterPhase, setEncounterPhase] = useState('');
  const [record, setRecord] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isChartStreaming, setIsChartStreaming] = useState(false);
  const [isVisitStreaming, setIsVisitStreaming] = useState(false);
  const [isChartRecording, setIsChartRecording] = useState(false);
  const [isVisitRecording, setIsVisitRecording] = useState(false);

  useMicrophoneBadge(isChartRecording || isVisitRecording);

  const [encounterStatus, setEncounterStatus] = useState('');
  const [currentActive, setCurrentActive] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [waitingForSocketConnection, setwaitingForSocketConnection] =
    useState(false);
  const [dictationStarted, setDictationStarted] = useState(false);
  const [localActiveButton, setLocalActiveButton] = useState(activeTab || '');
  const [isSummaryOutdated, setIsSummaryOutdated] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [isSegmentDisabled, setIsSegmentDisabled] = useState(false);
  const [restrictNoteAccess, setRestrictNoteAccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEHRResponseModalVisible, setIsEHRResponseModalVisible] =
    useState(false);
  const [ehrSubmitDocumentMessage, setEhrSubmitDocumentMessage] =
    useState(null);
  const [ehrSubmissionTooltip, setEhrSubmissionTooltip] = useState({
    open: false,
    message: '',
    status: 'idle',
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [templatePopupOpen, setTemplatePopupOpen] = useState(false);
  const [templateCount, setTemplateCount] = useState(0);
  const [manageDocumentDropdownVisible, setManageDropdownVisible] =
    useState(false);
  const [codingSubTab, setCodingSubTab] = useState('icd10');

  // Use external setActiveButton when in schedulepage mode, otherwise use local state
  const activeButton = schedulepage ? activeTab : localActiveButton;
  const setActiveButton = schedulepage
    ? externalSetActiveButton
    : setLocalActiveButton;
  const lastUpdatedSummaryRef = useRef(null);
  const editableSummaryRef = useRef(null);
  const isMagicEditingRef = useRef(null);
  const medicalConversationBoxRef = useRef(null);
  const initTranscript = useRef(false);
  const initActiveTab = useRef(false);
  const inVisitRef = useRef(null);
  const icd10CodingRef = useRef(null);

  const { isEntitled: isHumanScribeEntitled } =
    useFeatureEntitlements('custom-workflow');

  // Auto-resume visit logic for mobile
  const startRecordingDirectly = useCallback(() => {
    if (!encounterDetails?.encounter_id) {
      return;
    }

    if (inVisitRef.current?.handlePauseResumeToggle) {
      inVisitRef.current.handlePauseResumeToggle();
    } else {
      const data = {
        in_visit_status: 'inprogress',
        summary_verbosity:
          encounterDetails?.summary_verbosity ||
          currentPractitionerSettings?.ambient_in_visit_summary_verbosity,
        summary_format:
          encounterDetails?.summary_format ||
          currentPractitionerSettings?.ambient_in_visit_summary_format,
        template_id:
          encounterDetails?.in_visit_template_id ||
          currentPractitionerSettings?.ambient_in_visit_template_id,
        template_overrides_id:
          encounterDetails?.in_visit_template_overrides_id ||
          currentPractitionerSettings?.ambient_in_visit_template_overrides_id,
      };

      dispatch(
        updateEncounter(
          encounterDetails.encounter_id,
          'in-visit',
          data,
          storedParams,
          false
        )
      );

      // Initiate audio recording when inVisitRef is not available
      doStream({
        encounterId: encounterDetails.encounter_id,
        encounterPhase: 'in-visit',
        setMicLevel: () => {},
        setLiveTranscript,
        setLivePartialTranscript,
        selectedMicrophoneId: null,
        successCallback: () => {
          console.log('Auto-resume recording started successfully');
          setIsVisitStreaming(true);
          setIsVisitRecording(true);
        },
        failureCallback: (error) => {
          console.error('Failed to auto-resume recording:', error);
        },
        setDictation: () => {},
        isDictate: false,
        transcriptTimeoutCallback: (error) => {
          console.error('Auto-resume transcript timeout:', error);
        },
      });
    }
  }, [
    encounterDetails,
    currentPractitionerSettings,
    dispatch,
    storedParams,
    setLiveTranscript,
    setLivePartialTranscript,
    setIsVisitStreaming,
    setIsVisitRecording,
  ]);

  // Auto-resume effect
  useEffect(() => {
    if (autoResumeVisitEnabled && encounterDetails?.encounter_id) {
      const timer = setTimeout(() => {
        startRecordingDirectly();

        // Reset the autoResumeVisitEnabled state
        if (onAutoResumeComplete) {
          onAutoResumeComplete();
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [
    autoResumeVisitEnabled,
    encounterDetails?.encounter_id,
    startRecordingDirectly,
    onAutoResumeComplete,
  ]);
  const { isEntitled: isRestrictNoteAccessEntitled } =
    usePermissionEntitlements('locked-note-access');

  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);

  // Permission entitlements for start/resume encounters
  const { isEntitled: canStartInVisitEncounter } = usePermissionEntitlements(
    'encounter.in-visit.start'
  );
  const { isEntitled: canStartPreChartEncounter } = usePermissionEntitlements(
    'encounter.pre-chart.start'
  );
  const { isEntitled: canStartDictationEncounter } = usePermissionEntitlements(
    'encounter.dictation.start'
  );

  const isStartEncounterAllowed = useMemo(() => {
    return isAllowedToStartEncounter({
      encounterPhase,
      canStartInVisitEncounter,
      canStartPreChartEncounter,
    });
  }, [encounterPhase, canStartInVisitEncounter, canStartPreChartEncounter]);

  const { summaryList, dictateSummary, summaryExportLoading } = useSelector(
    (state) => state?.summaryState
  );

  const { getUuid } = useSelector((state) => state?.appointmentState);
  const { ehrSubmitDocument } = useSelector((state) => state?.ehrState);
  const isMobile = window.innerWidth < 1260;
  const isdesktop = window.innerWidth > 1260;

  const { persistMobileView } = useLocation().state || {};
  // 10 seconds refresh time interval
  const refreshInterval = 10_000;
  const autoSaveInterval = 5_000;

  const ambient_version = currentPractitionerSettings?.ambient_version;
  const { user } = useAuth();

  // encounter_id is already initialized from params at the top
  storeInLocal(
    `${currentPractitioner?.org_uuid}_topbar_encounter_id`,
    encounter_id
  );
  useEffect(() => {
    dispatch(setMobileRecord(true));
    if (schedulepage) {
      dispatch(resetEncounterState());
      dispatch(resetSummaryState());
    }
  }, [dispatch]);

  useEffect(() => {
    if (isdesktop && encounter_id && !persistMobileView) {
      navigate(`/encounters`);
    }
  }, [encounter_id, isdesktop, persistMobileView]);
  // End

  useEffect(() => {
    setEncounterStatus(getEncounterStatus(encounterDetails, encounterPhase));
    if (encounterDetails?.encounter_id) {
      configureRecordingTabs();
    }

    if (isHumanScribeEntitled && encounterDetails?.encounter_id) {
      if (
        ['in_progress', 'in_review', 'complete', 'shipped'].includes(
          encounterDetails?.encounter_status
        ) &&
        encounterDetails?.assigned_scribe_id !== null &&
        encounterDetails?.assigned_scribe_id !==
          currentPractitioner?.practitioner_id &&
        !isRestrictNoteAccessEntitled
      ) {
        setRestrictNoteAccess(true);
      } else {
        setRestrictNoteAccess(false);
      }
    }
  }, [
    isHumanScribeEntitled,
    encounterDetails?.encounter_id,
    encounterDetails?.encounter_status,
    encounterDetails?.assigned_scribe_id,
    currentPractitioner?.practitioner_id,
    encounterDetails,
    encounterPhase,
    isRestrictNoteAccessEntitled,
  ]);

  const selectInitialRecordingTab = (record) => {
    const { displayStatus } = formatEncounterStatus(record);
    if (displayStatus.startsWith('Pre-chart')) {
      setActiveButton('Pre-chart');
    } else {
      setActiveButton('In visit');
    }
  };

  const configureRecordingTabs = () => {
    let tabs;
    const { displayStatus } = formatEncounterStatus(encounterDetails);

    if (
      displayStatus === 'Completed' ||
      displayStatus === 'Copied to EHR' ||
      displayStatus === 'Saved to EHR'
    ) {
      tabs = [
        { label: 'Full note', value: 'In visit' },
        ...(settingsOrgValue?.org_settings?.enable_orders
          ? [{ label: 'Magic Orders', value: 'Orders' }]
          : []),
        ...(hasICD10Access || hasCPTAccess
          ? [{ label: 'Coding', value: 'Coding' }]
          : []),
        { label: 'Dictate', value: 'Dictate' },
        { label: 'Documents', value: 'Documents' },
      ];
    } else {
      tabs = [
        { label: 'In visit', value: 'In visit' },
        { label: 'Dictate', value: 'Dictate' },
      ];
    }

    if (encounterDetails?.pre_chart_status != null) {
      tabs.unshift({ label: 'Pre-chart', value: 'Pre-chart' });
    }

    setRecordingTabs(tabs);
  };
  useEffect(() => {
    switch (activeButton) {
      case 'Documents':
        setEncounterPhase('after-visit');
        break;
      case 'In visit':
        setEncounterPhase('in-visit');
        break;
      case 'Pre-chart':
        setEncounterPhase('pre-chart');
        break;
      case 'Orders':
        setEncounterPhase('orders');
        break;
      case 'Coding':
        setEncounterPhase('in-visit');
        break;
      case 'Dictate':
        setEncounterPhase('dictate');
        break;
      default:
        setEncounterPhase('in-visit');
    }
  }, [activeButton]);

  useEffect(() => {
    if (encounter_id) {
      dispatch(listSummaries(encounter_id, encounterPhase, 'ambient'));
      dispatch(listSummaries(encounter_id, encounterPhase, 'dictate'));
      dispatch(getTranscription(encounter_id, encounterPhase));
      initTranscript.current = false;
    }
  }, [encounter_id, encounterPhase, dispatch]);

  useEffect(() => {
    if (encounterDetails) {
      dispatch(selectedEncounterSlice(encounterDetails));
    }
    if (
      !initActiveTab.current &&
      encounterDetails?.encounter_id &&
      !schedulepage
    ) {
      selectInitialRecordingTab(encounterDetails);
      initActiveTab.current = true;
    }
  }, [encounterDetails]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!isMagicEditingRef.current && encounter_id) {
        dispatch(getEncounter(encounter_id));
        dispatch(listPinnedEncounters());
        dispatch(listSummaries(encounter_id, encounterPhase, 'ambient'));
        dispatch(listSummaries(encounter_id, encounterPhase, 'dictate'));
        dispatch(getTranscription(encounter_id, encounterPhase));
      }
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [encounter_id, encounterStatus, encounterPhase, dispatch]);

  useEffect(() => {
    setFullTranscript([]);
    setLiveTranscript([]);
    setLivePartialTranscript('');
    setIsSummaryOutdated(false);
    editableSummaryRef.current = null;
    initTranscript.current = false;
  }, [encounterPhase, encounter_id]);

  useEffect(() => {
    if (
      !initTranscript.current &&
      transcriptionbyIdValue?.length > 0 &&
      transcriptionbyIdValue[0]?.type == 'live'
    ) {
      let transcript = transcriptionbyIdValue[0]?.transcription_json;
      if (transcript?.length > 0) {
        setLiveTranscript(transcript);
      }

      initTranscript.current = true;
    }
  }, [transcriptionbyIdValue, encounter_id]);

  useEffect(() => {
    if (isChartStreaming || isChartRecording) setCurrentActive('Pre-chart');
    else if (isVisitStreaming || isVisitRecording) setCurrentActive('In-visit');
  }, [isChartRecording, isChartStreaming, isVisitRecording, isVisitStreaming]);

  useEffect(() => {
    if (encounter_id) {
      dispatch(partialResetEncounterState());
      dispatch(getEncounter(encounter_id));
      dispatch(listPinnedEncounters());
    }
  }, [encounter_id, encounterPhase, dispatch]);

  const transcriptcopy = () => {
    if (schedulepage) {
      analytics.track(
        'Clicked Copy As Rich Text in Ambient Transcript From VCA',
        {
          transcription: transcriptionbyIdValue[0]?.transcription_id,
          email: user?.email,
        }
      );
    } else {
      analytics.track(
        'Clicked Copy As Rich Text in Ambient Transcript From Aura',
        {
          transcription: transcriptionbyIdValue[0]?.transcription_id,
          email: user?.email,
        }
      );
    }

    const transcription = transcriptionbyIdValue[0];
    if (
      !transcription ||
      !transcription.transcription_json ||
      !transcription.transcription_json.length
    ) {
      toast.info('No transcription data available');
      return;
    }
    const encounterName = encounterDetails?.description || 'N/A';
    const createdAt = encounterDetails?.created_at
      ? moment(encounterDetails.created_at).format('MMM Do, YYYY')
      : 'N/A';
    const additionalInfo = `<strong>Encounter Name:</strong> ${encounterName}<br><strong>Date:</strong> ${createdAt}<br><br>`;
    const textToCopy =
      additionalInfo +
      transcriptionbyIdValue[0]?.transcription_json
        .map(
          (transcription) =>
            `Speaker ${transcription.speaker}:\n${transcription.text}`
        )
        .join('\n\n');
    const htmlToCopy = textToCopy.replace(/\n/g, '<br>');
    navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([textToCopy], { type: 'text/plain' }),
        'text/html': new Blob([htmlToCopy], { type: 'text/html' }),
      }),
    ]);
    toast.success('Transcription copied to clipboard');
  };

  const handleCopy = async () => {
    if (schedulepage) {
      analytics.track(
        'Clicked Copy As Rich Text In Ambient Medical Notes From VCA',
        {
          transcription: transcriptionbyIdValue[0]?.transcription_id,
          email: user?.email,
        }
      );
    } else {
      analytics.track(
        'Clicked Copy As Rich Text In Ambient Medical Notes From Aura',
        {
          transcription: transcriptionbyIdValue[0]?.transcription_id,
          email: user?.email,
        }
      );
    }

    if (
      (!editableSummaryRef.current?.summary_json ||
        editableSummaryRef.current?.summary_json?.trim() === '') &&
      !getUuid?.appointment_details?.llm_intake_summary_forms
    ) {
      toast.info('No data available to copy');
      return;
    }

    try {
      let medicalConversation =
        typeof editableSummaryRef.current?.summary_json === 'string'
          ? editableSummaryRef.current?.summary_json
              .replaceAll('\n', '<br>')
              .replaceAll('\\n', '<br>')
          : editableSummaryRef.current?.summary_json;

      if (
        getUuid?.appointment_details?.llm_intake_summary_forms &&
        encounterPhase === 'in-visit'
      ) {
        medicalConversation +=
          getUuid?.appointment_details?.llm_intake_summary_forms
            .replaceAll('\n', '<br>')
            .replaceAll('\\n', '<br>');
      }

      await copyRichTextToClipboard(
        sanitizeSummaryAsHtml(medicalConversation, {
          prefix: `<strong>Encounter Name:</strong> ${
            encounterDetails.description
          }<br><strong>Date:</strong> ${moment(
            encounterDetails.created_at
          ).format('MMM Do, YYYY')}`,
        })
      );

      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy medical notes');
    }
  };

  const handleMedicalNotesCopy = async () => {
    if (schedulepage) {
      analytics.track(
        'Clicked Copy All To Clipboard In Ambient Medical Notes From VCA',
        {
          transcription: transcriptionbyIdValue[0]?.transcription_id,
          email: user?.email,
        }
      );
    } else {
      analytics.track(
        'Clicked Copy All To Clipboard In Ambient Medical Notes From Aura',
        {
          transcription: transcriptionbyIdValue[0]?.transcription_id,
          email: user?.email,
        }
      );
    }

    if (
      (!editableSummaryRef.current?.summary_json ||
        editableSummaryRef.current?.summary_json?.trim() === '') &&
      !getUuid?.appointment_details?.llm_intake_summary_forms
    ) {
      toast.info('No data available to copy');
      return;
    }
    try {
      let medicalConversation = editableSummaryRef.current?.summary_json;

      if (
        getUuid?.appointment_details?.llm_intake_summary_forms &&
        encounterPhase === 'in-visit'
      ) {
        medicalConversation +=
          getUuid?.appointment_details?.llm_intake_summary_forms;
      }

      // Format encounter name and date
      const encounterName = encounterDetails.description || 'Unknown Encounter';
      const date = moment(encounterDetails.created_at).isValid()
        ? moment(encounterDetails.created_at).format('MMM Do, YYYY')
        : 'Unknown Date';

      let plainText = sanitizeSummaryAsText(medicalConversation);
      if (encounterPhase == 'dictate') {
        analytics.track('Clicked Copy All To Clipboard From Solo Dictation', {
          email: user?.email,
        });
      } else {
        plainText =
          `Encounter Name: ${encounterName}\nDate: ${date}\n\n` + plainText;
      }

      await copyToClipboard(plainText);

      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy medical notes');
    }
  };

  const MedicalCopyOptions = () => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'copy-editor') {
            handleCopy();
          }
        }}
      >
        <Menu.Item key="copy-editor">
          <span className="dropdown-text">Copy as rich text</span>
        </Menu.Item>
      </Menu>
    );
  };

  const TranscriptCopyOptions = () => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'transcript-text-editor') {
            transcriptcopy();
          }
        }}
      >
        <Menu.Item key="transcript-text-editor">
          <span className="dropdown-text">Copy as rich text</span>
        </Menu.Item>
      </Menu>
    );
  };

  const handleTranscriptionCopy = () => {
    if (schedulepage) {
      analytics.track(
        'Clicked Copy All To Clipboard In Ambient Transcript From VCA',
        {
          transcription: transcriptionbyIdValue[0]?.transcription_id,
          email: user?.email,
        }
      );
    } else {
      analytics.track(
        'Clicked Copy All To Clipboard In Ambient Transcript From Aura',
        {
          transcription: transcriptionbyIdValue[0]?.transcription_id,
          email: user?.email,
        }
      );
    }

    const transcription = transcriptionbyIdValue[0];
    if (
      !transcription ||
      !transcription.transcription_json ||
      !transcription.transcription_json.length
    ) {
      toast.info('No transcription data available');
      return;
    }

    // Format encounter name and date
    const encounterName = encounterDetails?.description || 'N/A';
    const createdAt = encounterDetails?.created_at
      ? moment(encounterDetails.created_at).format('MMM Do, YYYY')
      : 'N/A';
    const additionalInfo = `Encounter Name: ${encounterName}\nDate: ${createdAt}\n\n`;

    // Generate plain text transcription
    const textToCopy =
      additionalInfo +
      transcriptionbyIdValue[0]?.transcription_json
        .map(
          (transcription) =>
            `Speaker ${transcription.speaker}:\n${transcription.text}`
        )
        .join('\n\n');

    // Format HTML transcription for clipboard
    const htmlToCopy = textToCopy.replace(/\n/g, '<br>');

    // Write both plain text and HTML to clipboard
    navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([textToCopy], { type: 'text/plain' }),
        'text/html': new Blob([htmlToCopy], { type: 'text/html' }),
      }),
    ]);

    toast.success('Transcription copied to clipboard');
  };

  const handleDictation = () => {
    if (encounterPhase === 'dictate') {
      analytics.track('Clicked Start Dictation From Solo Dictation', {
        email: user?.email,
      });
    } else {
      analytics.track('Started dictation', { email: user?.email });
    }
    setwaitingForSocketConnection(true);
    doStream({
      encounterId: encounter_id,
      encounterPhase: '',
      setMicLevel: () => {},
      setLiveTranscript: null,
      setLivePartialTranscript: null,
      selectedMicrophoneId: medicalConversationBoxRef.current?.getMicrophone(),
      successCallback: () => {
        setwaitingForSocketConnection(false);
        setDictationStarted(true);
      },
      failureCallback: (error) => {
        setwaitingForSocketConnection(false);
        medicalConversationBoxRef.current?.handleSocketErrors(error);
      },
      setDictation: medicalConversationBoxRef.current?.setDictationText,
      isDictate: true,
      transcriptTimeoutCallback: () => {},
    });
  };

  useEffect(() => {
    setIsSummaryOutdated(false);

    const updateNewSummary = (summary) => {
      const cleaned_summary = {
        ...summary,
      };
      if (typeof summary.summary_json === 'string') {
        cleaned_summary.summary_json = summary.summary_json
          .replaceAll('\n', '<br>')
          .replaceAll('\\n', '<br>');
      }

      let updatedSummary = false;

      if (
        editableSummaryRef.current?.encounter_id !=
          cleaned_summary.encounter_id ||
        editableSummaryRef.current?.encounter_phase !=
          cleaned_summary.encounter_phase
      ) {
        updatedSummary = true;
      } else {
        if (editableSummaryRef.current.version != cleaned_summary.version) {
          if (editableSummaryRef.current.source === 'user') {
            setIsSummaryOutdated(true);
          } else {
            updatedSummary = true;
          }
        }
      }

      if (updatedSummary) {
        editableSummaryRef.current = cleaned_summary;
        setEditedSummary({ ...cleaned_summary });
        lastUpdatedSummaryRef.current = { ...cleaned_summary };
      }
    };

    // when the summary is generating or re-generation clear ref to summary
    // to ensured the updated summary is loaded when it's ready
    if (encounterStatus != 'completed' && encounterPhase !== 'dictate') {
      editableSummaryRef.current = null;
      setEditedSummary('');
      setIsSummaryOutdated(false);
    } else if (encounterStatus === 'completed' && summaryList?.length > 0) {
      const summary = summaryList.find(
        (summary) => summary.encounter_phase === encounterPhase
      );

      if (!summary) {
        return;
      }
      updateNewSummary(summary);
    } else if (encounterPhase === 'dictate' && dictateSummary?.length > 0) {
      const summary = dictateSummary.find(
        (summary) => summary.encounter_phase === encounterPhase
      );

      if (!summary) {
        return;
      }
      updateNewSummary(summary);
    }
  }, [summaryList, dictateSummary, encounterStatus, encounterPhase]);

  useEffect(() => {
    let saveTimeout;

    const autoSave = () => {
      // Handle race condition where the encounter is changed before the summary is savedAdd commentMore actions
      if (encounter_id !== editedSummary?.encounter_id) {
        return;
      }
      dispatch(
        updateSummary(
          encounter_id,
          encounterPhase,
          editedSummary?.summary_json,
          editedSummary?.version,
          lastUpdatedSummaryRef,
          editableSummaryRef,
          setEditedSummary,
          setIsSaving
        )
      );
    };

    if (
      (encounterStatus == 'completed' || encounterPhase == 'dictate') &&
      editedSummary?.summary_json != null &&
      lastUpdatedSummaryRef.current?.summary_json != null &&
      !isSummaryOutdated &&
      editedSummary?.summary_json !==
        lastUpdatedSummaryRef.current?.summary_json
    ) {
      clearTimeout(saveTimeout);
      saveTimeout = setTimeout(autoSave, autoSaveInterval);
      setIsSaving(true);
    }

    return () => clearTimeout(saveTimeout);
  }, [encounterStatus, editedSummary]);

  useEffect(() => {
    let timer;
    if (ehrSubmitDocument?.response) {
      if (ehrSubmitDocument.response.success) {
        setEhrSubmissionTooltip({
          open: true,
          message: 'Document submitted to EHR successfully!',
          status: 'success',
        });
        timer = setTimeout(() => {
          setEhrSubmissionTooltip({ open: false, message: '', status: 'idle' });
        }, 2000);
      } else {
        setEhrSubmissionTooltip({ open: false, message: '', status: 'idle' });
        setIsEHRResponseModalVisible(true);
        setEhrSubmitDocumentMessage(ehrSubmitDocument.response);
      }
      setIsSubmitting(false);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [ehrSubmitDocument]);

  const handleSummaryEdits = (value) => {
    if (editableSummaryRef.current) {
      editableSummaryRef.current.summary_json = value;
      setEditedSummary({
        ...editableSummaryRef.current,
        summary_json: value,
      });
    }
  };

  const showMicrophoneBar = useMemo(() => {
    if (!(isChartStreaming || isVisitStreaming)) return false;

    if (isVisitStreaming) return activeButton != 'In visit';

    if (isChartStreaming) return activeButton != 'Pre-chart';

    return true;
  }, [isChartStreaming, isVisitStreaming, activeButton]);

  useEffect(() => {
    if (dictationStarted) {
      endStream();
      setwaitingForSocketConnection(false);
      setDictationStarted(false);
      medicalConversationBoxRef.current?.stopDictation();
    }
  }, [encounter_id, activeButton]);

  useEffect(() => {
    return () => {
      endStream();
      setwaitingForSocketConnection(false);
      setDictationStarted(false);
      medicalConversationBoxRef.current?.stopDictation();
    };
  }, []);

  const handleExportPDF = () => {
    dispatch(exportSummaryPDF(encounter_id, encounterPhase));
  };

  const handlePauseResumeToggle = useCallback(() => {
    dispatch(
      updateEncounter(encounter_id, encounterPhase, {
        in_visit_status: 'inprogress',
      })
    );
    analytics.track('Clicked Resume after complete button for Aura', {
      encounter_id: encounter_id,
      email: user?.email,
    });
    inVisitRef.current?.handlePauseResumeToggle();
  }, [inVisitRef, inVisitRef.current, dispatch]);

  const filterByUpdateableFields = (data, details) => {
    const filtered = {};
    for (const section of Object.keys(data)) {
      if (!details[section]) continue;
      const sectionData = data[section];
      const sectionDetails = details[section];
      if (typeof sectionData !== 'object' || sectionData === null) continue;
      const filteredSection = {};
      for (const field of Object.keys(sectionData)) {
        if (sectionDetails[field]) {
          filteredSection[field] = sectionData[field];
        }
      }
      if (Object.keys(filteredSection).length > 0) {
        filtered[section] = filteredSection;
      }
    }
    return filtered;
  };

  const updateEncounterFromIframe = async () => {
    const summaryData = editableSummaryRef.current?.summary_json;

    if (!vimOS?.ehr?.resourceUpdater) {
      toast.error('Vim SDK not available');
      return;
    }

    const hasAnyUpdateableField = Object.values(updateableDetails).some(
      (section) =>
        typeof section === 'object' &&
        section !== null &&
        Object.values(section).some(Boolean)
    );

    logger.info('updateableDetails');
    logger.info(updateableDetails);

    if (!hasAnyUpdateableField) {
      toast.error('Cannot update encounter at this time');
      return;
    }

    setIsSubmitting(true);

    setEhrSubmissionTooltip({
      open: true,
      message: 'Submitting Diagnoses to EHR...',
      status: 'loading',
    });

    if (icd10CodingRef.current) {
      const icdResult = await icd10CodingRef.current.submitToEHR();
      if (!icdResult.success) {
        setEhrSubmissionTooltip({
          open: true,
          message: icdResult.message,
          status: 'error',
        });
        setIsSubmitting(false);
        return;
      }
    }

    setEhrSubmissionTooltip({
      open: true,
      message: 'Submitting Document to EHR...',
      status: 'loading',
    });

    const payload = {
      document_data: summaryData,
      summary_type: 'user',
      org_uuid: currentPractitioner?.org_uuid,
    };

    try {
      const res = await updateEncounterWithVimMappings(payload)(dispatch);
      logger.info('res');
      logger.info(res);
      if (res) {
        const filteredRes = filterByUpdateableFields(res, updateableDetails);
        logger.info('filteredRes');
        logger.info(filteredRes);
        await updateVimEncounter(filteredRes);
      }
      setEhrSubmissionTooltip({
        open: true,
        message: 'Document submitted to EHR successfully!',
        status: 'success',
      });
      setTimeout(() => {
        setEhrSubmissionTooltip({ open: false, message: '', status: 'idle' });
      }, 2000);
    } catch (error) {
      let errorMessage = 'Failed to submit to EHR';
      if (error?.type === 'validation_error') {
        errorMessage = 'Validation error: Please check the encounter data';
      } else if (error?.message) {
        errorMessage = `Failed: ${error.message}`;
      }

      setEhrSubmissionTooltip({
        open: true,
        message: errorMessage,
        status: 'error',
      });
      setTimeout(() => {
        setEhrSubmissionTooltip({ open: false, message: '', status: 'idle' });
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitToEHR = async () => {
    if (ehrSettings?.ehr_system === 'vim') {
      updateEncounterFromIframe();
      return;
    }
    analytics.track('Clicked Save To EHR Button', {
      encounter_id: encounterDetails?.encounter_id,
      email: user?.email,
    });

    setIsSubmitting(true);

    setEhrSubmissionTooltip({
      open: true,
      message: 'Submitting Diagnoses to EHR...',
      status: 'loading',
    });

    if (icd10CodingRef.current) {
      const icdResult = await icd10CodingRef.current.submitToEHR();
      if (!icdResult.success) {
        setEhrSubmissionTooltip({
          open: true,
          message: icdResult.message,
          status: 'error',
        });
        setIsSubmitting(false);
        return;
      }
    }

    setEhrSubmissionTooltip({
      open: true,
      message: 'Submitting Document to EHR...',
      status: 'loading',
    });

    const appointmentId =
      encounterDetails?.encounter_ehr_data?.appointment_id ||
      encounterDetails?.appointment_id;
    const data = {
      appointment_id: appointmentId,
      document_data: editableSummaryRef.current.summary_json,
      document_subclass: 'PROGRESSNOTE',
      encounter_ehr_id: encounterDetails?.ehr_id,
      encounter_id: encounterDetails?.encounter_id,
    };
    const submitDocumentHandler = submitDocumentToEHRIntegration(data);
    submitDocumentHandler(dispatch);
  };

  const handleModalOk = () => {
    setIsEHRResponseModalVisible(false);
    // Proceed with submission as a complete document
    setIsSubmitting(true);
    const appointmentId =
      encounterDetails?.encounter_ehr_data?.appointment_id ||
      encounterDetails?.appointment_id;
    const data = {
      appointment_id: appointmentId,
      document_data: editableSummaryRef.current.summary_json,
      document_subclass: 'PROGRESSNOTE', // Assuming this is the correct subclass
      skip_document_split: true,
      encounter_id: encounterDetails?.encounter_id,
    };
    const submitDocumentHandler = submitDocumentToEHRIntegration(data);
    submitDocumentHandler(dispatch).finally(() => {
      setIsSubmitting(false);
    });
  };

  const handleModalCancel = () => {
    setIsEHRResponseModalVisible(false);
  };

  if (!encounterDetails) {
    return null;
  }

  const { color, displayStatus } = formatEncounterStatus(encounterDetails);

  const getCurrentTemplate = () => {
    if (encounterPhase === 'pre-chart') {
      return {
        template_id: encounterDetails?.pre_chart_template_id,
        template_overrides_id:
          encounterDetails?.pre_chart_template_overrides_id,
      };
    } else if (encounterPhase === 'in-visit') {
      return {
        template_id: encounterDetails?.in_visit_template_id,
        template_overrides_id: encounterDetails?.in_visit_template_overrides_id,
      };
    } else if (encounterPhase === 'after-visit') {
      return {
        template_id: encounterDetails?.after_visit_template_id,
        template_overrides_id:
          encounterDetails?.after_visit_template_overrides_id,
      };
    }
  };

  // OP SSO check - disable Save to OP button if not launched from SSO
  const isOPSaveDisabled = isOP && !encounterDetails?.ehr_id;

  const getEhrSubmissionTooltip = () => {
    if (isOPSaveDisabled) {
      return 'Launch from patient chart in OP to enable saving';
    }
    if (isEhrSaveDelayed) {
      return 'Syncing chart details. Save option will unlock automatically when ready.';
    }
    if (ehrSubmissionTooltip.message) {
      return ehrSubmissionTooltip.message;
    }
    if (encounterDetails?.encounter_ehr_data?.document_submitted_at) {
      const submittedAt = moment
        .utc(encounterDetails.encounter_ehr_data.document_submitted_at)
        .local()
        .format('MM/DD/YYYY h:mm A');
      return `${isOP ? 'Submitted to OP' : 'Submitted to EHR'} at ${submittedAt}`;
    }
    return isOP ? 'Save to OP' : 'Save to EHR';
  };

  // Add rollback handler
  const handleRollback = (toSummary) => {
    if (schedulepage) {
      analytics.track('Clicked Rollback Summary From VCA', {
        encounter: record?.encounter_id,
        email: user?.email,
      });
    } else {
      analytics.track('Clicked Rollback Summary From Aura', {
        encounter: record?.encounter_id,
        email: user?.email,
      });
    }

    setIsRollingBack(true);
    dispatch(
      rollbackSummary(
        record?.encounter_id,
        encounterPhase,
        toSummary?.version,
        lastUpdatedSummaryRef,
        editableSummaryRef,
        setEditedSummary,
        setIsRollingBack
      )
    );
  };

  const handleOpenDrawer = () => {
    setDrawerOpen(true);
    dispatch(clearTemplateError());
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setTemplatePopupOpen(false);
  };

  if (activeButton === 'VCA summary') {
    if (showMicrophoneBar) {
      return (
        <div className="fixed bottom-[80px] z-50 bg-[#F5F5F5] w-[70%] sm:w-auto right-4 rounded-lg">
          <MicrophoneBar
            setActiveButton={setActiveButton}
            encounterPhase={encounterPhase}
            isStreaming={isChartStreaming || isVisitStreaming}
            currentActive={currentActive}
            isRecording={isChartRecording || isVisitRecording}
            setIsRecording={
              isChartRecording ? setIsChartRecording : setIsVisitRecording
            }
            setIsStreaming={
              isChartStreaming ? setIsChartStreaming : setIsVisitStreaming
            }
            schedulepage={schedulepage}
            selectedRecord={encounterDetails}
            storedParams={storedParams}
            setRecord={setRecord}
            setEncounterStatus={setEncounterStatus}
          />
        </div>
      );
    } else {
      return null; // Don't show anything when showMicrophoneBar is false
    }
  }

  const renderEncounterPanelContent = () => {
    const currentTranscriptProps = {
      encounterStatus,
      encounterPhase,
      storedParams,
      selectedRecord: encounterDetails,
      topBarInputs,
      setEncounterStatus,
      isStreaming,
      setIsStreaming,
      isRecording,
      setRecord,
      setIsRecording,
      isChartStreaming,
      isVisitStreaming,
      setIsChartStreaming,
      setIsVisitStreaming,
      isChartRecording,
      isVisitRecording,
      setIsChartRecording,
      setIsVisitRecording,
      setLiveTranscript,
      setLivePartialTranscript,
      restrictTemplates,
      patientDetails,
      ref: inVisitRef,
    };

    if (encounterPhase === 'pre-chart') {
      return (
        <div style={{ minHeight: '86vh' }} className="p-2">
          <CurrentTranscript {...currentTranscriptProps} />
        </div>
      );
    }

    // Render Orders component when on Orders tab
    if (encounterPhase === 'orders') {
      return (
        <div style={{ minHeight: '86vh' }}>
          <EncounterOrders
            encounterId={encounter_id}
            onSaveToEHR={() => {
              // TODO: Implement save to EHR functionality for orders
              console.log('Save orders to EHR');
            }}
          />
        </div>
      );
    }

    if (activeButton === 'Coding') {
      return null;
    }

    return encounterStatus === 'completed' ||
      encounterStatus === 'summary_inprogress' ||
      encounterPhase === 'dictate' ? (
      <div className=" ">
        <div className="mt-3 flex justify-center">
          <SummaryUpdatedStatus
            isSaving={isSaving}
            isSummaryOutdated={isSummaryOutdated}
            lastSaved={editableSummaryRef.current?.updated_at}
            encounterStatus={encounterStatus}
            encounterPhase={encounterPhase}
            isRollingBack={isRollingBack}
            onRollback={handleRollback}
          />
        </div>
        <MedicalConversationBox
          key={encounterPhase}
          ref={medicalConversationBoxRef}
          setIsSegmentDisabled={setIsSegmentDisabled}
          handleDictation={isDictationEntitled ? handleDictation : null}
          encounterStatus={encounterStatus}
          summaryVerbosity={encounterDetails?.summary_verbosity}
          encounterPhase={encounterPhase}
          selectedRecord={encounterDetails}
          selectedEncounterId={encounter_id}
          medicalConversation={editableSummaryRef.current?.summary_json}
          handleMedicalConversationChange={handleSummaryEdits}
          encounterId={encounter_id}
          summaryId={editableSummaryRef.current?.summary_id}
          activeButton={activeButton}
          schedulepage={schedulepage}
        />
        {encounterPhase !== 'dictate' && (
          <CurrentTranscript {...currentTranscriptProps} />
        )}
      </div>
    ) : (
      // View when an encounter is new or inprogress
      (encounterStatus !== 'completed' ||
        encounterStatus !== 'summary_inprogress' ||
        encounterStatus === 'inprogress') && (
        <div style={{ minHeight: '86vh' }}>
          <div
            className="flex flex-col items-center justify-center"
            style={{ minHeight: '60vh' }}
          >
            <CurrentTranscript {...currentTranscriptProps} />
          </div>

          {ambient_version == 2 &&
            (encounterStatus === 'inprogress' || encounterStatus === 'new') && (
              <div
                style={{
                  minHeight: '20vh',
                  maxHeight: '20vh',
                  overflowY: 'auto',
                }}
              >
                <Divider className="m-0 mt-4" />
                <MobileLiveTranscription
                  fullTranscript={fullTranscript}
                  setFullTranscript={setFullTranscript}
                  liveTranscript={liveTranscript}
                  livePartialTranscript={livePartialTranscript}
                  setLivePartialTranscript={setLivePartialTranscript}
                  encounterId={encounter_id}
                />
              </div>
            )}
        </div>
      )
    );
  };

  return (
    <>
      {!schedulepage && (
        <div
          className={`past flex flex-col relative ${
            !schedulepage ? 'page-wrapper' : ''
          }`}
        >
          <div className="relative">
            <div className={`bg-white rounded-xl shadow-md px-4 py-3`}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-[#000] text-lg font-semibold mx-2 mr-4 items-center flex">
                    {encounterDetails?.description}
                  </div>
                  <div>
                    <div
                      className="ml-2 flex gap-2"
                      style={{ color }}
                      id={displayStatus}
                    >
                      <img src={AmbientMicIcon} alt="mic logo" />
                      <span>{displayStatus}</span>
                    </div>

                    <div
                      className={
                        isMobile
                          ? 'flex gap-2 ml-2'
                          : 'flex gap-2 items-center text-[#7F7F7F] '
                      }
                    >
                      <CalanderAiIcon />
                      <span className="">
                        {moment(encounterDetails?.created_at).format(
                          'MMM Do, YYYY'
                        )}{' '}
                        {moment(encounterDetails?.created_at).format('h:mm A')}
                      </span>
                    </div>
                    <div className="ml-2">
                      <EncounterPatient
                        encounter={encounterDetails}
                        size="md"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={`past flex flex-col relative ${
          !schedulepage ? 'page-wrapper' : ''
        }`}
      >
        {restrictNoteAccess && (
          <div className="absolute inset-0 bg-gray-200/80 z-50 backdrop-blur-[5px]">
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-800">
                <div className="text-2xl font-bold mb-2 flex items-center gap-2">
                  <LockOutlined />
                  Access Restricted
                </div>
                <div className="text-gray-600">
                  You do not have access to this note.
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="relative">
          <div className="flex-1 grid current bg-white shadow-xl rounded-xl h-full">
            {!schedulepage && (
              <div className=" justify-between border-b border-gray-200 p-3">
                <SegmentedTabs
                  options={recordingTabs}
                  activeTab={activeButton}
                  setActiveTab={setActiveButton}
                  disabled={isSegmentDisabled}
                />
              </div>
            )}
            <LanguageSelector />
            {encounterPhase !== 'pre-chart' && activeButton !== 'Coding' && (
              <div className="mt-2 flex justify-center items-center">
                {(encounterStatus === 'completed' ||
                  encounterStatus === 'summary_inprogress' ||
                  encounterPhase === 'dictate') && (
                  <>
                    {encounterPhase === 'in-visit' && (
                      <Button
                        type={'button'}
                        className="bg-[#00D090] hover:bg-[#059669] flex items-center justify-center text-white font-normal p-2 py-2 px-4 rounded-xl focus:outline-none focus:shadow-outline mr-1"
                        onClick={handlePauseResumeToggle}
                        disabled={!isStartEncounterAllowed}
                      >
                        <span className="mr-1 mt-0">
                          <ResumeIcon />
                        </span>
                      </Button>
                    )}

                    {encounterPhase === 'dictate' ? (
                      <Button
                        type={'button'}
                        className="bg-[#00D090] hover:bg-[#059669] flex items-center justify-center text-white font-normal rounded-xl focus:outline-none focus:shadow-outline mr-2"
                        onClick={handleMedicalNotesCopy}
                      >
                        <Tooltip title="Copy all" placement="bottom">
                          <div className="flex items-center cursor-pointer text-xs gap-1">
                            <span className="ml-2">
                              <CopiesIcon height="17" width="17" />
                            </span>
                          </div>
                        </Tooltip>
                      </Button>
                    ) : (
                      <Dropdown.Button
                        type="primary"
                        onClick={handleMedicalNotesCopy}
                        icon={<ExpandIcon fill={'#ffffff'} />}
                        trigger={['click']}
                        style={{ width: 'fit-content' }}
                        rootClassName="copy-all rounded-xl drop-shadow-sm flex bg-[#00D090] hover:bg-[#059669]"
                        overlay={MedicalCopyOptions()}
                      >
                        <div className="flex cursor-pointer text-white ">
                          <span className="ml-2 justify-center">
                            <CopiesIcon />
                          </span>
                          {/* Copy */}
                        </div>
                      </Dropdown.Button>
                    )}

                    {encounterPhase !== 'dictate' && (
                      <span className="mr-1 ml-1">
                        <EncounterSummaryMagicEdit
                          editableSummaryRef={editableSummaryRef}
                          isMagicEditingRef={isMagicEditingRef}
                          searchFilters={{}}
                          encounterDetails={encounterDetails}
                          encounterPhase={encounterPhase}
                          restrictTemplates={restrictTemplates}
                          suggestions={suggestions}
                          ref={inVisitRef}
                        />
                      </span>
                    )}

                    {encounterPhase === 'after-visit' && (
                      <div className="flex justify-end mr-1">
                        <Tooltip title="Export note as PDF" placement="top">
                          <Button
                            type={'button'}
                            onClick={handleExportPDF}
                            loading={summaryExportLoading}
                            className={
                              'h-[35px] bg-[#E77A7A] hover:bg-[#991b1b]'
                            }
                            disabled={isSaving || isSummaryOutdated}
                          >
                            <span>
                              <div className="flex cursor-pointer gap-2">
                                {isMobile ? <PdfIcon /> : ''}
                              </div>
                            </span>
                          </Button>
                        </Tooltip>
                      </div>
                    )}
                    {encounterPhase === 'after-visit' &&
                      encounterStatus === 'completed' && (
                        <Dropdown
                          overlay={
                            <ul className="bg-white shadow-md rounded-md py-1 min-w-[140px]">
                              <li
                                className={`px-3 py-2 flex items-center ${
                                  templateCount < 2
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-gray-100 cursor-pointer'
                                }`}
                                onClick={(e) => {
                                  if (templateCount < 2) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                  }
                                  setManageDropdownVisible(false);
                                  handleOpenDrawer();
                                }}
                                style={{
                                  pointerEvents:
                                    templateCount < 2 ? 'none' : 'auto',
                                }}
                              >
                                <SwapOutlined className="mr-2" />
                                Select Another
                              </li>
                              <li
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                                onClick={() => {
                                  setManageDropdownVisible(false);
                                  setDrawerOpen(true);
                                  setTemplatePopupOpen(true);
                                }}
                              >
                                <FileAddOutlined className="mr-2" />
                                Generate New
                              </li>
                            </ul>
                          }
                          trigger={['click']}
                          placement="bottomRight"
                          open={manageDocumentDropdownVisible}
                          onOpenChange={setManageDropdownVisible}
                        >
                          <Button type="button" className="h-[35px] ml-1">
                            <span className="text-xs">Manage Docs</span>
                          </Button>
                        </Dropdown>
                      )}
                    {isDictationEntitled ? (
                      <div>
                        <Tooltip
                          title={!dictationStarted ? 'Dictate' : 'Stop'}
                          placement="bottom"
                        >
                          <div>
                            {!dictationStarted ? (
                              <Button
                                type={'button'}
                                onClick={handleDictation}
                                className={'h-[35px]'}
                                disabled={!canStartDictationEncounter}
                              >
                                <span>
                                  <div className="flex items-center cursor-pointer text-sm ">
                                    <div className="flex items-center">
                                      {waitingForSocketConnection ? (
                                        <Spin
                                          indicator={
                                            <LoadingOutlined
                                              style={{
                                                color: '#ffffff',
                                              }}
                                              spin
                                            />
                                          }
                                        />
                                      ) : (
                                        <>
                                          <DictateMic />
                                          <ProIcon />
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </span>
                              </Button>
                            ) : (
                              <Button
                                type={'button'}
                                onClick={() => {
                                  if (encounterPhase === 'dictate') {
                                    analytics.track(
                                      'Clicked Stop Dictation From Solo Dictation',
                                      { email: user?.email }
                                    );
                                  }
                                  endStream();
                                  setDictationStarted(false);
                                  setwaitingForSocketConnection(false);
                                  medicalConversationBoxRef.current?.stopDictation();
                                }}
                                className={
                                  'h-[35px] bg-[#d1112f] hover:bg-[#991b1b]'
                                }
                              >
                                <span>
                                  <div className="flex items-center cursor-pointer text-sm ">
                                    <div className="flex items-center">
                                      <DictateStopIcon />
                                    </div>
                                  </div>
                                </span>
                              </Button>
                            )}
                          </div>
                        </Tooltip>
                      </div>
                    ) : (
                      <>
                        <UpgradeTooltip>
                          <Button
                            type={'button'}
                            className={
                              'h-[35px] bg-gray-400 hover:bg-gray-500 cursor-not-allowed'
                            }
                          >
                            <span>
                              <div className="flex items-center text-sm ">
                                <div className="flex items-center">
                                  <>
                                    <DictateMic />
                                    <LockIcon />
                                  </>
                                </div>
                              </div>
                            </span>
                          </Button>
                        </UpgradeTooltip>
                      </>
                    )}
                    {ehrSettings?.ehr_system &&
                      encounterDetails?.encounter_ehr_data &&
                      encounterPhase !== 'dictate' &&
                      (ehrSettings?.ehr_system !== 'office_practicum' ||
                        isOPPushEhrEnabled) && (
                        <Tooltip
                          title={getEhrSubmissionTooltip()}
                          open={
                            isOPSaveDisabled || isEhrSaveDelayed
                              ? undefined
                              : ehrSubmissionTooltip.open
                          }
                          onOpenChange={(open) => {
                            if (isOPSaveDisabled || isEhrSaveDelayed) return;
                            setEhrSubmissionTooltip((prev) => ({
                              ...prev,
                              open: open,
                              message:
                                !open && prev.status === 'idle'
                                  ? ''
                                  : prev.message,
                              status:
                                !open && prev.status === 'idle'
                                  ? 'idle'
                                  : prev.status,
                            }));
                          }}
                        >
                          <div className="ml-1">
                            <EHRSubmissionDropdown
                              onSaveToEHR={handleSubmitToEHR}
                              isSubmitting={isSubmitting}
                              record={encounterDetails}
                              ehrSubmissionTooltip={ehrSubmissionTooltip}
                              practitionerSettings={currentPractitionerSettings}
                              currentTemplate={getCurrentTemplate()}
                              isSmallScreen={true}
                              disabled={isOPSaveDisabled || isEhrSaveDelayed}
                            />
                          </div>
                        </Tooltip>
                      )}
                  </>
                )}

                <div className="flex ">
                  {/*Button display for transcription tab */}
                  {(encounterStatus === 'completed' ||
                    encounterStatus === 'summary_inprogress') &&
                    activeButton === 'Transcription' && (
                      <>
                        <Dropdown.Button
                          type="primary"
                          onClick={handleTranscriptionCopy}
                          icon={<ExpandIcon fill={'#ffffff'} />}
                          trigger={['click']}
                          overlay={TranscriptCopyOptions}
                          placement="bottomLeft"
                          rootClassName="copy-all rounded-xl drop-shadow-sm flex bg-[#00D090] hover:bg-[#059669]"
                        >
                          <div className="flex items-center cursor-pointer gap-1 text-white font-normal">
                            <span>
                              <CopiesIcon />
                            </span>
                            <span>Copy all</span>
                          </div>
                        </Dropdown.Button>
                      </>
                    )}
                </div>
              </div>
            )}
            {renderEncounterPanelContent()}

            {/* ICD-10 always mounted (hidden when not on Coding tab) for ref stability during EHR submission */}
            <div
              style={{ display: activeButton === 'Coding' ? 'block' : 'none' }}
            >
              <div style={{ minHeight: '86vh' }} className="p-2">
                {hasICD10Access && hasCPTAccess && (
                  <div className="flex gap-2 mb-4 px-2 pt-2">
                    <button
                      onClick={() => setCodingSubTab('icd10')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        codingSubTab === 'icd10'
                          ? 'bg-[#00D090] text-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      ICD-10
                    </button>
                    <button
                      onClick={() => setCodingSubTab('cpt')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        codingSubTab === 'cpt'
                          ? 'bg-[#00D090] text-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      CPT
                    </button>
                  </div>
                )}
                {hasICD10Access && (
                  <div
                    style={{
                      display:
                        !hasCPTAccess || codingSubTab === 'icd10'
                          ? 'block'
                          : 'none',
                    }}
                  >
                    <ICD10Coding
                      ref={icd10CodingRef}
                      encounterStatus={encounterStatus}
                      allowed={hasICD10Access}
                    />
                  </div>
                )}
                {hasCPTAccess && (
                  <div
                    style={{
                      display:
                        !hasICD10Access || codingSubTab === 'cpt'
                          ? 'block'
                          : 'none',
                    }}
                  >
                    <CPTCodes
                      encounterStatus={encounterStatus}
                      allowed={hasCPTAccess}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {encounterPhase === 'after-visit' && encounterStatus === 'completed' && (
        <TemplateTableWrapper
          record={encounterDetails}
          encounterPhase={encounterPhase}
          restrictTemplates={restrictTemplates}
          selectedRecord={encounterDetails}
          setEncounterStatus={setEncounterStatus}
          schedulepage={schedulepage}
          searchFilters={storedParams}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          templatePopupOpen={templatePopupOpen}
          setTemplatePopupOpen={setTemplatePopupOpen}
          handleCloseDrawer={handleCloseDrawer}
          setTemplateCount={setTemplateCount}
        />
      )}

      <EHRResponseModal
        isVisible={isEHRResponseModalVisible}
        onConfirm={handleModalOk}
        onCancel={handleModalCancel}
        response={ehrSubmitDocumentMessage}
      />
      {showMicrophoneBar && activeButton != 'VCA summary' ? (
        <div className="fixed bottom-[80px] z-50 bg-[#F5F5F5] w-[70%] sm:w-auto right-4 rounded-lg">
          <MicrophoneBar
            encounterPhase={encounterPhase}
            setActiveButton={setActiveButton}
            isStreaming={isChartStreaming || isVisitStreaming}
            currentActive={currentActive}
            isRecording={isChartRecording || isVisitRecording}
            setIsRecording={
              isChartRecording ? setIsChartRecording : setIsVisitRecording
            }
            setIsStreaming={
              isChartStreaming ? setIsChartStreaming : setIsVisitStreaming
            }
            schedulepage={schedulepage}
            selectedRecord={encounterDetails}
            storedParams={storedParams}
            setRecord={setRecord}
            setEncounterStatus={setEncounterStatus}
          />
        </div>
      ) : null}

      {!isWrapped && <SetPageHeader />}
    </>
  );
};

export default MobileEncounterDetails;
